import React, { useMemo } from 'react';

import { HealthParameterThreshold } from '../../../../entities/HealthParameter';
import AlertSeverityEnum from '../../../../enums/AlertSeverityEnum';
import HealthParameterEnum from '../../../../enums/HealthParameterEnum';
import getAlertLabels from '../../../../utils/getAlertLabels';
import { describeThreshold } from '../../../../utils/describeThreshold';

import { Container, HeadRow, Intro, Row, Rule, Table, Title } from './styles';

interface ParametersModalProps {
  thresholds: HealthParameterThreshold[];
}

/** Os parâmetros da régua (`02-alertas.md` §1), na ordem em que a enfermagem os lê. */
const PARAMETERS: HealthParameterEnum[] = [
  HealthParameterEnum.BLOOD_PRESSURE,
  HealthParameterEnum.RESTING_HEART_RATE,
  HealthParameterEnum.BLOOD_OXYGEN,
  HealthParameterEnum.BODY_TEMPERATURE,
  HealthParameterEnum.SLEEP,
  HealthParameterEnum.GLUCOSE,
  HealthParameterEnum.WEIGHT_LOSS,
];

/**
 * **"Ver parâmetros"** — a régua clínica que classifica cada leitura.
 *
 * Mostra só a régua **geral e ativa**, a mesma que `rulerFor` usa na linha da tabela.
 */
const ParametersModal: React.FC<ParametersModalProps> = ({ thresholds }) => {
  const rows = useMemo(() => {
    const applied = thresholds.filter(
      (threshold) =>
        threshold.parameterSet === 'alerta' &&
        (threshold.careLine ?? null) === null &&
        threshold.active,
    );

    const bySeverity = (
      parameter: HealthParameterEnum,
      severity: AlertSeverityEnum,
    ): string =>
      applied
        .filter(
          (threshold) =>
            threshold.parameter === parameter &&
            threshold.severity === severity,
        )
        .map(describeThreshold)
        .join(' ou ');

    return PARAMETERS.map((parameter) => ({
      parameter,
      moderate: bySeverity(parameter, AlertSeverityEnum.MODERATE),
      severe: bySeverity(parameter, AlertSeverityEnum.SEVERE),
    })).filter((row) => row.moderate || row.severe);
  }, [thresholds]);

  return (
    <Container>
      <Title>Parâmetros e limiares</Title>
      <Intro>
        A régua que classifica cada leitura. Cada linha da tabela de alertas
        mostra o trecho desta régua que disparou.
      </Intro>

      <Table>
        <HeadRow>
          <span>Parâmetro</span>
          <span>Moderado</span>
          <span>Grave</span>
        </HeadRow>

        {rows.map((row) => (
          <Row key={row.parameter}>
            <span>{getAlertLabels.parameter(row.parameter)}</span>
            <Rule empty={!row.moderate}>{row.moderate || '—'}</Rule>
            <Rule empty={!row.severe}>{row.severe || '—'}</Rule>
          </Row>
        ))}
      </Table>
    </Container>
  );
};

export default ParametersModal;
